import { graphql } from 'react-relay';
import { useMemo, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { WatchQueryFetchPolicy } from 'apollo-client';

import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useAccessLinkPageDataProviderShareableLinkPageQuery$data,
	useAccessLinkPageDataProviderShareableLinkPageQuery$variables,
} from './__generated__/useAccessLinkPageDataProviderShareableLinkPageQuery.graphql.ts';
import { PublicLinkPageStatus } from './__types__/AccessLinkPageTypes.ts';
import type {
	RefetchType,
	AccessLinkPageDataCalculated,
	AccessLinkPageProviderData,
	AccessLinkAccessType,
} from './__types__/AccessLinkPageTypes.ts';
import type { AccessLinkContentQueryData } from './useAccessLinkContentQuery.ts';
import { useAccessLinkContentQuery } from './useAccessLinkContentQuery.ts';
import type { AccessLinkPermissionsForObjectQueryData } from './useAccessLinkPermissionsForObject.ts';
import { useAccessLinkPermissionsForObject } from './useAccessLinkPermissionsForObject.ts';

const ENROLLED_CONTENT_TYPES = ['page', 'whiteboard'] as const;

const KNOWN_ERROR_MESSAGES = [
	'This is a Standard/Premium feature. You are currently subscribed to the Free plan.',
	'Anonymous access is not permitted',
];

const i18n = defineMessages({
	accessLinkUrlPathError: {
		id: 'shareable-links.useAccessLinkPageDataProvider.sharePathError.message.non-final',
		defaultMessage: 'Access link status was ON, but no URL path was provided',
		description: 'Error message when the access link status is ON, but no share url is provided.',
	},
});

type AccessLinkPageData = {
	pageStatus: PublicLinkPageStatus | string;
	publicLinkUrlPath: string | null | undefined;
	accessType: AccessLinkAccessType | string | null | undefined;
};

type AccessLinkPageQueryData = {
	isLoading: boolean;
	error?: Error;
	data?: AccessLinkPageData | null | undefined;
	refetch: () => void;
};

type AccessLinkContentQueryProps =
	// if you want to skip useAccessLinkContentQuery, then a contentType must be provided since it's crucial for computing certain fields
	| {
			skip: true;
			contentType: string;
	  }
	| {
			skip: false;
	  };

/**
 * Computes the full access link state from the three query results.
 *
 * Combines page status, content type, and permissions data to determine:
 * - Whether access links are admin-enabled, fully enabled, and allowed for the content type
 * - Whether the current user can edit access link settings
 * - The access type (INTERNAL_LICENSED, EXTERNAL, or null)
 */
const computeAccessLinkState = ({
	pageData,
	contentData,
	permissionsData,
	isAccessLinkStatusOn,
	enrolledContentTypes,
	edition,
}: {
	pageData: AccessLinkPageQueryData;
	contentData: AccessLinkContentQueryData;
	permissionsData: AccessLinkPermissionsForObjectQueryData;
	isAccessLinkStatusOn: boolean;
	enrolledContentTypes: readonly string[];
	edition: ConfluenceEdition | null;
}): AccessLinkPageDataCalculated => {
	// Are access links allowed for this content type
	const isAccessLinkAllowedForContentType =
		!contentData.error &&
		contentData.data !== undefined &&
		enrolledContentTypes.includes(contentData.data.contentType);

	// Are editable access links allowed for this content type
	const isAccessLinkEditAllowedForContentType =
		isAccessLinkAllowedForContentType &&
		edition === ConfluenceEdition.PREMIUM &&
		contentData.data?.contentType === 'whiteboard';

	// Can current user edit access link status
	const canEditAccessLink = !contentData.error && contentData.data?.canEditAccessLink === true;

	// Does the access link have edit permissions (e.g. editable public whiteboards)
	const isEditAccessLink = !!(!permissionsData.error && permissionsData.data?.hasEditPermission);

	// Are access links enabled by admins at site and space levels
	const isAccessLinkAdminEnabled =
		isAccessLinkStatusOn ||
		(!pageData.error && pageData.data?.pageStatus === PublicLinkPageStatus.OFF);

	// Is access link allowed for content type, admin enabled, and status ON
	const isAccessLinkFullyEnabled =
		isAccessLinkStatusOn && isAccessLinkAdminEnabled && isAccessLinkAllowedForContentType;

	// The access scope of the access link
	const accessType = isAccessLinkStatusOn
		? ((pageData.data?.accessType as AccessLinkAccessType | null | undefined) ?? null)
		: null;

	const refetch = (dataToFetch: RefetchType = 'all') => {
		// if dataToFetch !== permission, it means it's either all or public-link
		if (dataToFetch !== 'permission') {
			pageData.refetch();
		}
		// if dataToFetch !== public-link, it means it's either all or permission
		if (dataToFetch !== 'public-link' && isAccessLinkEditAllowedForContentType) {
			permissionsData.refetch();
		}
	};
	return {
		isAccessLinkAdminEnabled,
		isAccessLinkFullyEnabled,
		isAccessLinkAllowedForContentType,
		isAccessLinkEditAllowedForContentType,
		isEditAccessLink,
		canEditAccessLink,
		accessLinkStatus: pageData.data?.pageStatus as PublicLinkPageStatus | undefined,
		accessLinkSharePath: pageData.data?.publicLinkUrlPath,
		accessType,
		refetch,
	};
};

/**
 * Orchestrates access link data fetching for content.
 *
 * Combines three queries (page status, content type, and permissions) into a single
 * `AccessLinkPageProviderData` shape consumed by the share-and-restrict dialog.
 * Extends the public-links provider with `accessType` support to distinguish between
 * INTERNAL_LICENSED (workspace) and EXTERNAL (public) links.
 */
export const useAccessLinkPageDataProvider = (props: {
	pageId: string;
	skip?: boolean;
	contentQueryProps?: AccessLinkContentQueryProps;
	fetchPolicy?: WatchQueryFetchPolicy;
}): AccessLinkPageProviderData => {
	const intl = useIntl();

	const isLicensed = useIsLicensed();
	const { edition } = useSessionData();

	const contentDataRaw = useAccessLinkContentQuery({
		contentId: props.pageId,
		skip: props.skip || props.contentQueryProps?.skip,
	});

	const contentType = props.contentQueryProps?.skip
		? props.contentQueryProps?.contentType
		: contentDataRaw?.data?.contentType;

	const contentData: AccessLinkContentQueryData = useMemo(
		() =>
			contentDataRaw.isLoading
				? { isLoading: true }
				: {
						...contentDataRaw,
						data: contentDataRaw.data
							? {
									...contentDataRaw.data,
									contentType: contentType ?? '',
								}
							: {
									canEditAccessLink: false,
									contentType: contentType ?? '',
								},
					},
		[contentDataRaw, contentType],
	);

	const shouldSkip =
		props.skip ||
		!(ENROLLED_CONTENT_TYPES as readonly string[]).includes(contentData?.data?.contentType || '') ||
		!isLicensed ||
		edition === ConfluenceEdition.FREE;

	const accessLinkPageQueryResult = useQueryInterop<
		useAccessLinkPageDataProviderShareableLinkPageQuery$data,
		useAccessLinkPageDataProviderShareableLinkPageQuery$variables
	>(
		graphql`
			query useAccessLinkPageDataProviderShareableLinkPageQuery($pageId: ID!) {
				publicLinkPage(pageId: $pageId) {
					pageId
					pageStatus
					publicLinkUrlPath
					accessType
				}
			}
		`,
		{
			variables: { pageId: props.pageId },
			fetchPolicy: props.fetchPolicy,
			skip: shouldSkip,
		},
	);

	const permissionsData = useAccessLinkPermissionsForObject({
		objectId: props.pageId,
		skip:
			shouldSkip ||
			edition !== ConfluenceEdition.PREMIUM ||
			contentData?.data?.contentType !== 'whiteboard',
	});

	const pageData: AccessLinkPageQueryData = useMemo(
		() => ({
			isLoading: accessLinkPageQueryResult.loading,
			data: accessLinkPageQueryResult.data?.publicLinkPage,
			error: accessLinkPageQueryResult.error,
			refetch: accessLinkPageQueryResult.refetch,
		}),
		[
			accessLinkPageQueryResult.loading,
			accessLinkPageQueryResult.data,
			accessLinkPageQueryResult.error,
			accessLinkPageQueryResult.refetch,
		],
	);

	useEffect(() => {
		if (
			isUnauthorizedError(accessLinkPageQueryResult.error) ||
			// We currently aren't being given error types by the backend.
			// When we do, we can better parse these expected errors.
			KNOWN_ERROR_MESSAGES.some((msg) => accessLinkPageQueryResult.error?.message?.includes(msg))
		) {
			markErrorAsHandled(accessLinkPageQueryResult.error);
		}
	}, [accessLinkPageQueryResult.error]);

	// Is access link status for page ON
	const isAccessLinkStatusOn =
		!pageData.error && pageData.data?.pageStatus === PublicLinkPageStatus.ON;

	const loading = pageData.isLoading || contentData.isLoading || permissionsData.isLoading;

	const errors = useMemo(() => {
		const queryErrors = [pageData, contentData, permissionsData]
			.map((query) => !query.isLoading && query.error)
			.filter((err): err is Error => Boolean(err));

		if (isAccessLinkStatusOn && pageData.data?.publicLinkUrlPath === null) {
			queryErrors.push(new Error(intl.formatMessage(i18n.accessLinkUrlPathError)));
		}

		return queryErrors;
	}, [pageData, contentData, permissionsData, isAccessLinkStatusOn, intl]);

	// When the provider is explicitly skipped (e.g. experiment is OFF), return a
	// static empty result immediately. All hooks above still execute (React rules
	// of hooks) but their queries are skipped, so this avoids running the
	// computeAccessLinkState derivation on empty data.
	if (props.skip) {
		return {
			isLoading: false,
			errors: [],
			isAccessLinkAdminEnabled: false,
			isAccessLinkFullyEnabled: false,
			isAccessLinkAllowedForContentType: false,
			isAccessLinkEditAllowedForContentType: false,
			isEditAccessLink: false,
			canEditAccessLink: false,
			accessLinkStatus: null,
			accessLinkSharePath: null,
			accessType: null,
			refetch: () => {},
		};
	}

	if (loading)
		return {
			isLoading: true,
			errors,
		};

	return {
		isLoading: false,
		errors,
		...computeAccessLinkState({
			pageData,
			contentData,
			permissionsData,
			isAccessLinkStatusOn,
			enrolledContentTypes: ENROLLED_CONTENT_TYPES,
			edition,
		}),
	};
};
